<template>
  <div>
    <div
      v-for="section in extractSections(attachments)"
      :key="section.id"
      class="flex flex-col mb-4"
    >
      <div class="mb-4">
        <span v-if="section?.title" class="text-primary font-bold">{{ section.title }}</span>
        <p class="flex flex-1">
          {{ section.description }}
        </p>
      </div>
      <div class="flex flex-row gap-8 flex-wrap px-2 items-start">
        <div
          v-for="attachment in attachmentsForSection(attachments, section)"
          :key="attachment.id"
        >
          <div v-if="attachment.relevant">
            <attachment-card
              v-if="hasAction(attachment)"
              :attachment="attachment"
              :tint-color="tintColor"
            />
            <attachment-card-without-action
              v-else
              :attachment="attachment"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="flex flex-col w-full">
      <div
        v-if="extractSections(attachments).length !== 0 && attachmentsWithoutSection(attachments).length > 0"
        class="mb-4"
      >
        <span class="text-primary font-bold">Autres ressources</span>
      </div>
      <div class="flex flex-row gap-8 flex-wrap px-2 items-start">
        <div
          v-for="attachment in attachmentsWithoutSection(attachments)"
          :key="attachment.id"
        >
          <div v-if="attachment.relevant">
            <attachment-card
              v-if="hasAction(attachment)"
              :attachment="attachment"
              :tint-color="tintColor"
              :consumer="consumer"
            />
            <attachment-card-without-action
              v-else
              :attachment="attachment"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import AttachmentCard from '@/attachment/components/AttachmentCard.vue';
import AttachmentCardWithoutAction from '@/attachment/components/AttachmentCardWithoutAction.vue';
import {Attachment, AttachmentSection} from "@/attachment/interfaces/attachment";
import Customer from "@/customer/interfaces/Customer";
import Beneficiary from "@/customer/interfaces/Beneficiary";

const {attachments, tintColor = "#00000030", consumer} = defineProps<{
    attachments: Attachment[];
    tintColor?: string;
    consumer?: Customer | Beneficiary;
}>();

/**
 * Check if the attachment has an action associated with it.
 */
const hasAction = (attachment: Attachment) => {
    return attachment.type || attachment.external_link;
};

/**
 * Extract the sections from the given attachments.
 */
const extractSections = (attachments: Attachment[]): AttachmentSection[] => {
    return attachments.reduce((sections: AttachmentSection[], attachment) => {
        const attachmentSections = attachment.sections;

        attachmentSections.forEach(attachmentSection => {
            const section = sections.find(_section => _section.id === attachmentSection.id);

            if (!section) {
                sections.push(attachmentSection);
            }
        });

        return sections;
    }, []);
};

/**
 * Get the attachments for the given section.
 */
const attachmentsForSection = (attachments: Attachment[], section: AttachmentSection) => {
    const filteredAttachments = attachments.filter(attachment => {
        return attachment.sections.find(_section => _section.id === section.id);
    });

    return orderAttachments(filteredAttachments);
};

/**
 * Get the attachments without section.
 */
const attachmentsWithoutSection = (attachments: Attachment[]) => {
    const filteredAttachments = attachments.filter(attachment => {
        return attachment.sections.length === 0;
    });

    return orderAttachments(filteredAttachments);
};

/**
 * Order the attachments with related sale first, then the ones with action, and finally the ones without action.
 */
const orderAttachments = (attachments: Attachment[]) => {
    return attachments.sort((a, b) => {
        if (a.related_sale && !b.related_sale) {
            return -1;
        }

        if (!a.related_sale && b.related_sale) {
            return 1;
        }


        if (hasAction(a) && !hasAction(b)) {
            return -1;
        }

        if (!hasAction(a) && hasAction(b)) {
            return 1;
        }

        return 0;
    });
};
</script>
